import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MessageSquare, Sun, StickyNote, CheckSquare, FileText, Users, Building2, CalendarDays,
  Handshake, Star, Bot, ListFilter, Send, Settings, UserPlus, HelpCircle, FileBarChart,
  Plus, ChevronDown, ChevronLeft, ChevronRight, ArrowLeftRight, Shield,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { useTerminology } from "@/contexts/TerminologyContext";
import { CURRENT_WORKSPACE_ID } from "@/utils/local_storage_keys";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useBilling } from "@/lib/hooks/use-billing";
import { useIsSuperuser, useIsReadOnly } from "@/lib/rbac/hooks";
import { AppSwitch } from "@/utils/app-switch";

const staticMainNavKeys = [
  { key: "nav.today", icon: Sun, path: "/" },
  { key: "nav.meetings", icon: CalendarDays, path: "/meetings" },
  { key: "nav.notes", icon: FileText, path: "/notes" },
  { key: "nav.tasks", icon: CheckSquare, path: "/tasks" },
];

const footerNavKeys = [
  { key: "nav.settings", icon: Settings, path: "/settings" },
  { key: "nav.inviteTeammates", icon: UserPlus, path: "/invite" },
  { key: "nav.help", icon: HelpCircle, path: "/help" },
];


// Fallback color mapping for workspaces without logos
const getWorkspaceColor = (name: string) => {
  const colors = ["bg-primary", "bg-emerald-600", "bg-blue-600", "bg-purple-600", "bg-orange-600"];
  const index = name.length % colors.length;
  return colors[index];
};

export default function AppSidebar() {
  const { t } = useTranslation();
  const { terminology, singular } = useTerminology();
  const isReadOnly = useIsReadOnly();
  const showFavorites = useFeatureFlag(FEATURE_FLAGS.FAVORITES);
  const showReports = useFeatureFlag(FEATURE_FLAGS.REPORTS);
  const showAgents = useFeatureFlag(FEATURE_FLAGS.AGENTS);
  const showAskCoffee = useFeatureFlag(FEATURE_FLAGS.ASK_COFFEE);
  // Lead finder and campaigns are mutation-heavy features (creating lists,
  // sending outbound). Hide from read-only users.
  const showLeadFinder = useFeatureFlag(FEATURE_FLAGS.LEAD_FINDER) && !isReadOnly;
  const showCampaigns = !isReadOnly;
  const staticMainNav = staticMainNavKeys.map(n => ({ ...n, label: t(n.key) }));
  const footerNav = footerNavKeys.map(n => ({ ...n, label: t(n.key) }));

  const mainNav = [
    ...(showAskCoffee ? [{ label: t("nav.askCoffee"), icon: MessageSquare, path: "#ask-coffee" }] : []),
    ...staticMainNav,
  ];

  const crmNav = [
    { label: terminology.people, icon: Users, path: "/people" },
    { label: terminology.companies, icon: Building2, path: "/companies" },
    { label: terminology.deals, icon: Handshake, path: "/deals" },
    ...(showReports ? [{ label: t("nav.reports"), icon: FileBarChart, path: "/reports" }] : []),
    ...(showFavorites ? [{ label: t("nav.favorites"), icon: Star, path: "/favorites" }] : []),
  ];

  const bottomNav = [
    ...(showAgents ? [{ label: t("nav.agents"), icon: Bot, path: "/agents" }] : []),
    ...(showLeadFinder ? [{ label: t("nav.leadFinder"), icon: ListFilter, path: "/list-builder" }] : []),
    ...(showCampaigns ? [{ label: t("nav.campaigns"), icon: Send, path: "/campaigns" }] : []),
  ];

  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch workspaces from current user (avoids duplicate query)
  const { currentUser, loading } = useCurrentUser();
  const workspaces = currentUser?.workspaces || [];
  const isSuperuser = useIsSuperuser();

  // For superusers, split workspaces into member vs. other
  const { memberWorkspaces, otherWorkspaces } = useMemo(() => {
    if (!isSuperuser) return { memberWorkspaces: workspaces, otherWorkspaces: [] as typeof workspaces };
    const member = workspaces.filter(ws => ws.isMember !== false);
    const other = workspaces.filter(ws => ws.isMember === false);
    return {
      memberWorkspaces: [...member].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
      otherWorkspaces: [...other].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    };
  }, [isSuperuser, workspaces]);

  // Workspace search state for superuser selector
  const [workspaceSearch, setWorkspaceSearch] = useState("");

  // Determine if user should see "Switch to Classic" based on signup date
  // Uses lastLogin as proxy since User type doesn't have createdAt
  const showSwitchToClassic = (() => {
    if (!currentUser?.lastLogin) return true;
    const signupThreshold = new Date("2026-03-31T23:59:59");
    return new Date(currentUser.lastLogin) < signupThreshold;
  })();

  // Stabilize workspaces array reference for dependencies
  const workspacesKey = workspaces.map(w => w.id).join(',');

  // Get billing information for trial banner
  const { billingInfo } = useBilling();

  // Track broken images to fallback to initials — reset on each mount (page refresh)
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  // Reset broken images when workspaces change (e.g., after refresh)
  useEffect(() => {
    setBrokenImages(new Set());
  }, [workspacesKey]);
  // Track logo overrides from settings page uploads
  const [logoOverrides, setLogoOverrides] = useState<Record<string, string>>({});

  // Listen for workspace logo updates from settings
  useEffect(() => {
    const handleLogoUpdate = (e: Event) => {
      const { workspaceId, logoUrl } = (e as CustomEvent).detail;
      if (workspaceId && logoUrl) {
        setLogoOverrides(prev => ({ ...prev, [workspaceId]: logoUrl }));
        setBrokenImages(prev => { const next = new Set(prev); next.delete(workspaceId); return next; });
      }
    };
    window.addEventListener('workspaceLogoUpdated', handleLogoUpdate);
    return () => window.removeEventListener('workspaceLogoUpdated', handleLogoUpdate);
  }, []);

  // Get current workspace from localStorage or default to first workspace
  const getCurrentWorkspaceId = () => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(CURRENT_WORKSPACE_ID);
  };

  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(getCurrentWorkspaceId);

  // Find active workspace object
  const activeWorkspace = workspaces.find(ws => ws.id === currentWorkspaceId) || workspaces[0];

  // Handle image load errors
  const handleImageError = (workspaceId: string) => {
    setBrokenImages(prev => new Set(prev).add(workspaceId));
  };

  // Check if workspace logo should be shown
  const getLogoUrl = (workspace: { id: string; logoUrl?: string | null }) => {
    return logoOverrides[workspace.id] || workspace.logoUrl || null;
  };
  const shouldShowLogo = (workspace: { id: string; logoUrl?: string | null }) => {
    return !!getLogoUrl(workspace) && !brokenImages.has(workspace.id);
  };

  // Set default workspace on first load
  useEffect(() => {
    if (workspaces.length > 0 && !currentWorkspaceId) {
      const firstWorkspaceId = workspaces[0].id;
      localStorage.setItem(CURRENT_WORKSPACE_ID, firstWorkspaceId);
      setCurrentWorkspaceId(firstWorkspaceId);
      // Notify useWorkspaceId consumers so workspace-scoped state initializes
      // immediately. In preview mode this is the only path that sets the ID.
      window.dispatchEvent(new CustomEvent('workspaceChanged', { detail: { workspaceId: firstWorkspaceId } }));
    }
  }, [workspacesKey, currentWorkspaceId, workspaces]);

  // Handle workspace change
  const handleWorkspaceChange = (workspaceId: string) => {
    localStorage.setItem(CURRENT_WORKSPACE_ID, workspaceId);
    setCurrentWorkspaceId(workspaceId);
    // Dispatch custom event to notify other components of workspace change
    window.dispatchEvent(new CustomEvent('workspaceChanged', { detail: { workspaceId } }));
    // Optionally reload the page to clear any workspace-specific cached data
    // window.location.reload();
  };

  const NavItem = ({ item, shortcut, onCustomClick }: { item: { label: string; icon: any; path: string }; shortcut?: string; onCustomClick?: () => void }) => {
    const Icon = item.icon;
    const active = !onCustomClick && (location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path)));
    return (
      <button
        onClick={() => onCustomClick ? onCustomClick() : navigate(item.path)}
        className={cn(
          "flex items-center gap-3 w-full rounded-md px-3 py-2 text-sm font-medium transition-colors",
          active
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground hover:bg-sidebar-accent/60"
        )}
        title={collapsed ? item.label : undefined}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && (
          <>
            <span className="truncate flex-1 text-left">{item.label}</span>
            {shortcut && (
              <kbd className="hidden sm:inline-flex h-5 items-center rounded border border-sidebar-border bg-sidebar px-1 text-[10px] font-mono text-sidebar-muted shrink-0">
                {shortcut}
              </kbd>
            )}
          </>
        )}
      </button>
    );
  };

  const AddMenuItems = () => (
    <>
      <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "person" })), 0); }} className="gap-2"><Users className="h-4 w-4" /> {singular.people}</DropdownMenuItem>
      <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "company" })), 0); }} className="gap-2"><Building2 className="h-4 w-4" /> {singular.companies}</DropdownMenuItem>
      <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "deal" })), 0); }} className="gap-2"><Handshake className="h-4 w-4" /> {singular.deals}</DropdownMenuItem>
      <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "note" })), 0); }} className="gap-2"><StickyNote className="h-4 w-4" /> {t("common.note")}</DropdownMenuItem>
      <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "task" })), 0); }} className="gap-2"><CheckSquare className="h-4 w-4" /> {t("common.task")}</DropdownMenuItem>
    </>
  );

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "relative flex flex-col border-r border-sidebar-border bg-sidebar h-full transition-all duration-200 shrink-0",
        collapsed ? "w-16" : "w-60"
      )}
    >
      {/* Collapse toggle – centered on the vertical divider */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute top-[14px] -right-3 z-10 h-5 w-5 rounded-full border border-sidebar-border bg-sidebar flex items-center justify-center text-sidebar-muted hover:text-sidebar-foreground hover:bg-muted transition shadow-sm"
      >
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </button>

      {/* Header */}
      <div className="border-b border-sidebar-border">
        <div className="flex items-center justify-between px-3 h-[49px]">
          {workspaces.length > 1 ? (
            <DropdownMenu onOpenChange={(open) => { if (!open) setWorkspaceSearch(""); }}>
              <DropdownMenuTrigger className={cn(
                "flex items-center gap-2 text-sm font-semibold text-sidebar-foreground hover:opacity-80 max-w-full min-w-0 overflow-hidden",
                collapsed && "justify-center"
              )}>
                {loading ? (
                  <div className="h-7 w-7 rounded-md bg-muted animate-pulse shrink-0" />
                ) : activeWorkspace ? (
                  <div className="relative h-7 w-7 shrink-0">
                    <div className={cn("h-7 w-7 rounded-md flex items-center justify-center text-xs font-bold text-white", getWorkspaceColor(activeWorkspace.name))}>
                      {activeWorkspace.name.charAt(0).toUpperCase()}
                    </div>
                    {getLogoUrl(activeWorkspace) && !brokenImages.has(activeWorkspace.id) && (
                      <img
                        src={getLogoUrl(activeWorkspace)!}
                        alt={activeWorkspace.name}
                        className="absolute inset-0 h-7 w-7 rounded-md object-cover"
                        onError={() => handleImageError(activeWorkspace.id)}
                      />
                    )}
                  </div>
                ) : null}
                {!collapsed && !loading && activeWorkspace && (
                  <>
                    <span className="truncate min-w-0 text-left" title={activeWorkspace.name}>
                      {activeWorkspace.name}
                    </span>
                    {isSuperuser && activeWorkspace.subscription?.status === 'trialing' && (
                      <span className="text-[10px] font-semibold text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded shrink-0">
                        TRIAL
                      </span>
                    )}
                    <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
                  </>
                )}
              </DropdownMenuTrigger>
              {isSuperuser ? (
                <DropdownMenuContent align="start" className="w-[280px] p-0" onCloseAutoFocus={(e) => e.preventDefault()}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t("nav.searchWorkspaces")}
                      value={workspaceSearch}
                      onValueChange={setWorkspaceSearch}
                    />
                    <CommandList className="max-h-[350px]">
                      <CommandEmpty>{t("nav.noWorkspacesFound")}</CommandEmpty>
                      {memberWorkspaces.filter(ws => !workspaceSearch || ws.name?.toLowerCase().includes(workspaceSearch.toLowerCase())).length > 0 && (
                        <CommandGroup heading={t("nav.myWorkspaces")}>
                          {memberWorkspaces
                            .filter(ws => !workspaceSearch || ws.name?.toLowerCase().includes(workspaceSearch.toLowerCase()))
                            .map(ws => (
                              <CommandItem
                                key={ws.id}
                                value={ws.id}
                                onSelect={() => handleWorkspaceChange(ws.id)}
                                className="gap-2 cursor-pointer"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <div className="relative h-5 w-5 shrink-0">
                                    <div className={cn("h-5 w-5 rounded flex items-center justify-center text-[10px] font-bold text-white", getWorkspaceColor(ws.name ?? ''))}>
                                      {(ws.name ?? '').charAt(0).toUpperCase()}
                                    </div>
                                    {getLogoUrl(ws) && !brokenImages.has(ws.id) && (
                                      <img
                                        src={getLogoUrl(ws)!}
                                        alt={ws.name ?? ''}
                                        className="absolute inset-0 h-5 w-5 rounded object-cover"
                                        onError={() => handleImageError(ws.id)}
                                      />
                                    )}
                                  </div>
                                  <span className="truncate min-w-0 text-left flex-1" title={ws.name ?? undefined}>{ws.name}</span>
                                  {isSuperuser && ws.subscription?.status === 'trialing' && (
                                    <span className="text-[10px] font-semibold text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded shrink-0">
                                      TRIAL
                                    </span>
                                  )}
                                  {ws.subscription?.pricingTier && (
                                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                                      {ws.subscription.pricingTier}
                                    </span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      )}
                      {otherWorkspaces.filter(ws => !workspaceSearch || ws.name?.toLowerCase().includes(workspaceSearch.toLowerCase())).length > 0 && (
                        <CommandGroup heading={t("nav.allWorkspaces")}>
                          {otherWorkspaces
                            .filter(ws => !workspaceSearch || ws.name?.toLowerCase().includes(workspaceSearch.toLowerCase()))
                            .map(ws => (
                              <CommandItem
                                key={ws.id}
                                value={ws.id}
                                onSelect={() => handleWorkspaceChange(ws.id)}
                                className="gap-2 cursor-pointer"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <div className="relative h-5 w-5 shrink-0">
                                    <div className={cn("h-5 w-5 rounded flex items-center justify-center text-[10px] font-bold text-white", getWorkspaceColor(ws.name ?? ''))}>
                                      {(ws.name ?? '').charAt(0).toUpperCase()}
                                    </div>
                                    {getLogoUrl(ws) && !brokenImages.has(ws.id) && (
                                      <img
                                        src={getLogoUrl(ws)!}
                                        alt={ws.name ?? ''}
                                        className="absolute inset-0 h-5 w-5 rounded object-cover"
                                        onError={() => handleImageError(ws.id)}
                                      />
                                    )}
                                  </div>
                                  <span className="truncate min-w-0 text-left flex-1" title={ws.name ?? undefined}>{ws.name}</span>
                                  {isSuperuser && ws.subscription?.status === 'trialing' && (
                                    <span className="text-[10px] font-semibold text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded shrink-0">
                                      TRIAL
                                    </span>
                                  )}
                                  {ws.subscription?.pricingTier && (
                                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                                      {ws.subscription.pricingTier}
                                    </span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </DropdownMenuContent>
              ) : (
                <DropdownMenuContent align="start" className="max-w-[250px] max-h-[400px] overflow-y-auto">
                  {[...workspaces].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')).map(ws => (
                    <DropdownMenuItem
                      key={ws.id}
                      onClick={() => handleWorkspaceChange(ws.id)}
                      className="gap-2 cursor-pointer"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="relative h-5 w-5 shrink-0">
                          <div className={cn("h-5 w-5 rounded flex items-center justify-center text-[10px] font-bold text-white", getWorkspaceColor(ws.name ?? ''))}>
                            {(ws.name ?? '').charAt(0).toUpperCase()}
                          </div>
                          {getLogoUrl(ws) && !brokenImages.has(ws.id) && (
                            <img
                              src={getLogoUrl(ws)!}
                              alt={ws.name ?? ''}
                              className="absolute inset-0 h-5 w-5 rounded object-cover"
                              onError={() => handleImageError(ws.id)}
                            />
                          )}
                        </div>
                        <span className="truncate min-w-0 text-left" title={ws.name ?? undefined}>{ws.name}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              )}
            </DropdownMenu>
          ) : (
            <div className={cn(
              "flex items-center gap-2 text-sm font-semibold text-sidebar-foreground max-w-full min-w-0 overflow-hidden",
              collapsed && "justify-center"
            )}>
              {loading ? (
                <div className="h-7 w-7 rounded-md bg-muted animate-pulse shrink-0" />
              ) : activeWorkspace ? (
                <div className="relative h-7 w-7 shrink-0">
                  <div className={cn("h-7 w-7 rounded-md flex items-center justify-center text-xs font-bold text-white", getWorkspaceColor(activeWorkspace.name))}>
                    {activeWorkspace.name.charAt(0).toUpperCase()}
                  </div>
                  {getLogoUrl(activeWorkspace) && !brokenImages.has(activeWorkspace.id) && (
                    <img
                      src={getLogoUrl(activeWorkspace)!}
                      alt={activeWorkspace.name}
                      className="absolute inset-0 h-7 w-7 rounded-md object-cover"
                      onError={() => handleImageError(activeWorkspace.id)}
                    />
                  )}
                </div>
              ) : null}
              {!collapsed && !loading && activeWorkspace && (
                <span className="truncate min-w-0 text-left" title={activeWorkspace.name}>
                  {activeWorkspace.name}
                </span>
              )}
            </div>
          )}
        </div>
        {/* Superuser mode indicator */}
        {isSuperuser && !collapsed && (
          <div className="flex items-center gap-1.5 px-3 pb-2 -mt-1">
            <Shield className="h-3 w-3 text-amber-500" />
            <span className="text-[11px] text-amber-500 font-medium">{t("nav.superuserMode")}</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-2 py-1 space-y-1 scrollbar-thin">
        {showAskCoffee && (
          <>
            <NavItem
              item={mainNav[0]}
              shortcut="⌘K"
              onCustomClick={() => window.dispatchEvent(new CustomEvent("openAskCoffee"))}
            />
            <div className="my-2 h-px bg-sidebar-border" />
          </>
        )}
        {staticMainNav.map((item) => <NavItem key={item.path} item={item} />)}
        <div className="my-2 h-px bg-sidebar-border" />
        {crmNav.map((item) => <NavItem key={item.path} item={item} />)}
        <div className="my-2 h-px bg-sidebar-border" />
        {bottomNav.map((item) => <NavItem key={item.path} item={item} />)}
      </nav>

      {/* Free trial banner */}
      {billingInfo?.isInTrial && billingInfo.trialDaysRemaining !== null && activeWorkspace?.name !== "Gazillion" && (
        <div className="px-2 py-2">
          <button
            onClick={() => navigate("/settings?section=billing")}
            className={cn(
              "w-full rounded-md border border-accent/30 bg-accent/5 px-3 py-2 text-left transition hover:bg-accent/10",
              collapsed && "px-0 flex items-center justify-center"
            )}
          >
            {collapsed ? (
              <span className="text-[10px] font-bold text-accent">{billingInfo.trialDaysRemaining}</span>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("common.daysOfTrialRemaining", { count: billingInfo.trialDaysRemaining })}
              </p>
            )}
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-sidebar-border px-2 py-2 space-y-0.5">
        {footerNav.map((item) => <NavItem key={item.path} item={item} />)}
        {showSwitchToClassic && (
          <NavItem
            item={{ label: t("nav.switchToClassic"), icon: ArrowLeftRight, path: "" }}
            onCustomClick={() => currentWorkspaceId && AppSwitch.goToLegacy(currentWorkspaceId)}
          />
        )}
      </div>
    </aside>
  );
}
